import type { UnwrapRef, WatchSource } from 'vue'

export function chunk<T>(array: T[], size: number): T[][] {
  const chunkedArr: T[][] = []
  let index: number = 0
  while (index < array.length) {
    chunkedArr.push(array.slice(index, size + index))
    index += size
  }
  return chunkedArr
}

export function arrayify<T>(obj: T | T[]): T[] {
  if (Array.isArray(obj))
    return obj
  return [obj]
}

export function unique<T>(array: T[]): T[] {
  return array.filter((e, i) => array.indexOf(e) === i)
}

export function remove<T>(arrayToRemoveFrom: T[], elementsToRemove: T[]): T[] {
  return arrayToRemoveFrom.filter(item => !elementsToRemove.includes(item))
}

export function containsAllElements<T>(
  mainArray: T[],
  secondArray: T[],
): boolean {
  // returns true if all elements of secondArray are in mainArray
  return secondArray.every(elem => mainArray.includes(elem))
}

export function getWatchSourceValue<T>(value: WatchSource<T>): T {
  if (isRef(value))
    return value.value
  return value()
}

interface ArrayMap<T, Data> {
  get: (key: T) => UnwrapRef<Data> | undefined
  getAsync: (key: T) => Promise<UnwrapRef<Data> | undefined>
  ready: (key: T) => boolean
  clear: () => void
  all: Map<T, Data>
}

export function useArrayScope<T, Data>(
  value: WatchSource<T[]>,
  data: (val: T) => Data | Promise<Data>,
  keepOld = false,
  unreactive = false,
): ArrayMap<T, Data> {
  let map: Map<T, Data>
  if (unreactive) {
    map = new Map<T, Data>()
  }
  else {
    map = reactive(new Map<T, Data>()) as Map<T, Data>
  }

  watch(
    value,
    (_newVal) => {
      void fixEntries()
    },
    { immediate: true, deep: true },
  )

  async function fixEntries(): Promise<void> {
    const val = getWatchSourceValue(value)
    const keys = [...map.keys()]
    const missingKeys = keys.filter(k => !val.includes(k))

    if (!keepOld) {
      for (const key of missingKeys) {
        map.delete(key)
      }
    }

    const promises: Array<Promise<void>> = []
    for (const key of val) {
      if (!map.has(key)) {
        promises.push(
          Promise.resolve(data(key)).then((p) => {
            map.set(key, p)
          }),
        )
      }
    }
    await Promise.all(promises)
  }

  function clear(): void {
    map.clear()
    void fixEntries()
  }

  return {
    get: (key: T) => unref(map.get(key)) as UnwrapRef<Data> | undefined,
    getAsync: async (key: T) => {
      if (map.has(key))
        return unref(map.get(key)) as UnwrapRef<Data> | undefined
      const d = await data(key)
      map.set(key, d)
      return unref(d) as UnwrapRef<Data> | undefined
    },
    ready: (key: T) => map.has(key),
    clear,
    all: map,
  }
}
